import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, X, CheckCircle2, Clock, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";

function StatusPill({ status }) {
  const submitted = status === "submitted";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${submitted ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
      {submitted ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      {submitted ? "Submitted" : "Draft"}
    </span>
  );
}

export default function Responses() {
  const { user } = useAuth();
  const [subs, setSubs] = useState([]);
  const [schema, setSchema] = useState(null);
  const [detail, setDetail] = useState(null);
  const [exporting, setExporting] = useState("");
  const isAdmin = user?.role === "admin";

  const loadSubs = async () => {
    try { const { data } = await api.get("/responses"); setSubs(data); } catch (e) { toast.error(apiErr(e)); }
  };

  const exportFile = async (fmt) => {
    setExporting(fmt);
    try {
      const { data } = await api.get(`/admin/export/${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `wema_iam_responses.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(""); }
  };

  useEffect(() => {
    loadSubs();
    api.get("/survey/schema").then(({ data }) => setSchema(data)).catch(() => {});
  }, []);

  const openDetail = async (email) => {
    try { const { data } = await api.get(`/responses/${encodeURIComponent(email)}`); setDetail(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="min-h-screen pb-20" data-testid="responses-page">
      <TopBar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: "var(--burgundy)" }}>
              <ClipboardList className="w-7 h-7" /> Responses
            </h1>
            <p className="text-neutral-500 mt-1 text-sm">
              {isAdmin
                ? "All submissions from authorized respondents, including drafts in progress."
                : "Submitted questionnaires from the analyst team, read-only."}
            </p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <button data-testid="export-csv-btn" onClick={() => exportFile("csv")} disabled={!!exporting} className="btn btn-outline">
                {exporting === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Export CSV
              </button>
              <button data-testid="export-pdf-btn" onClick={() => exportFile("pdf")} disabled={!!exporting} className="btn btn-primary">
                {exporting === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Export PDF
              </button>
            </div>
          )}
        </div>

        <div className="card-surface overflow-hidden mt-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f6f1e6] text-left text-neutral-600">
                <th className="px-4 py-3 font-semibold">Respondent</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Progress</th>
                <th className="px-4 py-3 font-semibold">Apps</th>
                <th className="px-4 py-3 font-semibold">{isAdmin ? "Updated" : "Submitted"}</th>
              </tr>
            </thead>
            <tbody data-testid="responses-table">
              {subs.map((s) => (
                <tr key={s.email} className="border-t border-[hsl(var(--border))] hover:bg-[#faf6f1] cursor-pointer" onClick={() => openDetail(s.email)} data-testid={`response-row-${s.email}`}>
                  <td className="px-4 py-3 font-medium">{s.email}</td>
                  <td className="px-4 py-3"><StatusPill status={s.status} /></td>
                  <td className="px-4 py-3 text-neutral-600">{s.answered}/{s.total}</td>
                  <td className="px-4 py-3 text-neutral-600">{s.app_count}</td>
                  <td className="px-4 py-3 text-neutral-500">{(s.submitted_at || s.updated_at) ? new Date(s.submitted_at || s.updated_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
              {!subs.length && <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400" data-testid="responses-empty">No responses yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {detail && schema && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setDetail(null)} data-testid="response-detail">
          <div className="w-full max-w-2xl bg-[var(--parchment)] h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="brand-band text-white px-6 py-4 flex items-center justify-between sticky top-0">
              <div>
                <p className="text-xs text-amber-200 uppercase tracking-widest">Response</p>
                <p className="font-semibold">{detail.email}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-white/80 hover:text-white" data-testid="close-response-detail-btn"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-6">
              <StatusPill status={detail.status} />
              {schema.sections.map((s) => (
                <div key={s.id}>
                  {(s.type === "repeatable" || s.questions) && (
                    <h3 className="font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: "var(--burgundy)" }}>
                      <span className="section-badge !w-6 !h-6 !text-xs">{s.id}</span>{s.title}
                    </h3>
                  )}
                  {s.type === "repeatable" ? (
                    (detail.apps || []).filter((a) => Object.values(a || {}).some((v) => (v || "").trim())).map((a, i) => (
                      <div key={i} className="card-surface p-3 mb-2 text-sm">
                        <p className="font-semibold text-[var(--burgundy-2)] mb-1">Application {i + 1}</p>
                        {s.fields.map((f) => (a[f.id] || "").trim() && (
                          <p key={f.id} className="mb-1"><span className="text-neutral-500">{f.label}: </span>{a[f.id]}</p>
                        ))}
                      </div>
                    ))
                  ) : s.questions ? (
                    s.questions.map((q) => (
                      <div key={q.id} className="mb-2 text-sm">
                        <p className="text-neutral-500">{q.label}</p>
                        <p className="text-neutral-800">{(detail.answers?.[q.id] || "").trim() || <span className="text-neutral-300">—</span>}</p>
                      </div>
                    ))
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
